import React from "react";
import { Modal, Box, Typography, IconButton, Fade, Backdrop, Divider, Chip } from "@mui/material";
import { 
    Close as CloseIcon, 
    Info as InfoIcon, 
    Security as SecurityIcon, 
    Business as BusinessIcon,
    Email as EmailIcon 
} from "@mui/icons-material";

const MLSSystemDetails = ({ visible, onClose }) => {
    const sections = [
        {
            id: 'northstar',
            title: 'NorthStar MLS',
            icon: <InfoIcon />,
            content: 'Based on information submitted to the MLS GRID as of 10/21/2024. All data is obtained from various sources and may not have been verified by broker or MLS GRID. Supplied Open House Information is subject to change without notice. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Some IDX listings have been excluded from this website.',
            color: 'primary'
        },
        {
            id: 'dmca',
            title: 'DMCA Notice',
            icon: <SecurityIcon />,
            content: 'The Digital Millennium Copyright Act of 1998, 17 U.S.C. § 512 (the "DMCA") provides recourse for copyright owners who believe that material appearing on the Internet infringes their rights under U.S. copyright law. If you believe in good faith that any content or material made available in connection with our website or services infringes your copyright, you (or your agent) may send us a notice requesting that the content or material be removed, or access to it blocked.',
            color: 'warning'
        },
        {
            id: 'canopy',
            title: 'Canopy MLS / Carolina Homes',
            icon: <BusinessIcon />,
            content: 'Based on information submitted to the MLS GRID as of 10/21/2024. All data is obtained from various sources and may not have been verified by broker or MLS GRID. Supplied Open House Information is subject to change without notice. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Some IDX listings have been excluded from this website.',
            color: 'success'
        }
    ];

    const dmcaRequirements = [
        'Description of the copyrighted work that is the subject of claimed infringement',
        'Description of the alleged infringing content and information sufficient to permit us to locate the content',
        'Contact information for you, including your address, telephone number and email address',
        'A statement by you that you have a good faith belief that the content in the manner complained of is not authorized by the copyright owner, or its agent, or by the operation of any law',
        'A statement by you, signed under penalty of perjury, that the information in the notification is accurate and that you have the authority to enforce the copyrights that are claimed to be infringed',
        'A physical or electronic signature of the copyright owner or a person authorized to act on the copyright owner\'s behalf'
    ];

    return (
        <Modal 
            open={visible} 
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, sm: 3 }
            }}
        >
            <Fade in={visible} timeout={500}>
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: 24,
                        width: { xs: '95%', sm: '85%', md: '75%', lg: '65%', xl: '55%' },
                        maxWidth: '900px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative'
                    }}
                >
                    {/* Enhanced Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: { xs: 2, sm: 3 },
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <InfoIcon sx={{ fontSize: 32 }} />
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
                                    MLS Information
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Multiple Listing Service Details & Legal Information
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton 
                            onClick={onClose}
                            sx={{ 
                                color: 'white',
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                '&:hover': { 
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    transform: 'scale(1.05)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                            aria-label="Close modal"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content Area */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: { xs: 2, sm: 3 }
                        }}
                    >
                        {/* Sections */}
                        {sections.map((section, index) => (
                            <Box key={section.id} sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Chip 
                                        icon={section.icon}
                                        label={section.title}
                                        color={section.color}
                                        variant="outlined"
                                        sx={{ 
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            height: 40
                                        }}
                                    />
                                </Box>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        lineHeight: 1.7,
                                        color: 'text.primary',
                                        fontSize: '0.95rem',
                                        pl: 1
                                    }}
                                >
                                    {section.content}
                                </Typography>
                                {index < sections.length - 1 && <Divider sx={{ mt: 3 }} />}
                            </Box>
                        ))}

                        {/* DMCA Requirements Section */}
                        <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Chip 
                                    icon={<EmailIcon />}
                                    label="DMCA Notice Requirements"
                                    color="error"
                                    variant="filled"
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    mb: 2, 
                                    color: 'text.secondary',
                                    fontStyle: 'italic'
                                }}
                            >
                                Contact: <strong>DMCAnotice@MLSGrid.com</strong>
                            </Typography>
                            <Typography 
                                variant="body2" 
                                sx={{ mb: 2, color: 'text.primary' }}
                            >
                                The DMCA requires that your notice of alleged copyright infringement include:
                            </Typography>
                            <Box component="ol" sx={{ pl: 2 }}>
                                {dmcaRequirements.map((requirement, index) => (
                                    <Box 
                                        component="li" 
                                        key={index}
                                        sx={{ 
                                            mb: 1.5,
                                            typography: 'body2',
                                            lineHeight: 1.6,
                                            color: 'text.primary'
                                        }}
                                    >
                                        {requirement}
                                    </Box>
                                ))}
                            </Box>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    display: 'block',
                                    mt: 2,
                                    p: 2,
                                    bgcolor: 'warning.light',
                                    color: 'warning.contrastText',
                                    borderRadius: 1,
                                    fontWeight: 500
                                }}
                            >
                                ⚠️ Failure to include all of the above information may result in the delay of the processing of your complaint.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            p: { xs: 2, sm: 3 },
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                            Information last updated: October 21, 2024
                        </Typography>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default MLSSystemDetails;
